import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import {
  DecodedDefaultRuleConfigSchema,
  DefaultRuleConfigUpdateSchema,
} from "../default-rules/config";

/**
 * Wire response for a single default-rule config row. The discriminated
 * union from `DecodedDefaultRuleConfigSchema` narrows `configurations`
 * per `defaultRuleId` on the client side, so callers can build typed
 * batch updates from the query result without any assertions.
 */
export const DefaultRuleConfigResponse = DecodedDefaultRuleConfigSchema;
export type DefaultRuleConfigResponse = typeof DefaultRuleConfigResponse.Type;

const GetDefaultRulesResponse = Schema.Struct({
  configs: Schema.Array(DefaultRuleConfigResponse),
});

const BatchUpdateDefaultRuleSortIndexesRequest = Schema.Struct({
  orders: Schema.Array(
    Schema.Struct({ defaultRuleId: Schema.String, sortIndex: Schema.Number }),
  ),
});

/**
 * Wire payload for `batchUpdateDefaultRuleConfigs`. The item schema is
 * a discriminated union keyed on `defaultRuleId`, so the HTTP layer
 * rejects any `configurations` that doesn't match the rule's schema
 * before the handler runs.
 */
const BatchUpdateDefaultRuleConfigsRequest = Schema.Struct({
  updates: Schema.Array(DefaultRuleConfigUpdateSchema),
});

const GetRuleCollisionAcknowledgmentsResponse = Schema.Struct({
  keys: Schema.Array(Schema.String),
});

const RuleCollisionAcknowledgmentRequest = Schema.Struct({
  collisionKey: Schema.String,
});

export const DefaultRuleGroup = HttpApiGroup.make("defaultRule")
  .add(
    endpoint.merchant
      .get("getDefaultRules", "/default-rules")
      .addSuccess(GetDefaultRulesResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("batchUpdateDefaultRuleSortIndexes", "/default-rules/sort-indexes")
      .setPayload(BatchUpdateDefaultRuleSortIndexesRequest)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("batchUpdateDefaultRuleConfigs", "/default-rules/configs")
      .setPayload(BatchUpdateDefaultRuleConfigsRequest)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getRuleCollisionAcknowledgments",
        "/rule-collisions/acknowledgments",
      )
      .addSuccess(GetRuleCollisionAcknowledgmentsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("acknowledgeRuleCollision", "/rule-collisions/acknowledge")
      .setPayload(RuleCollisionAcknowledgmentRequest)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("unacknowledgeRuleCollision", "/rule-collisions/unacknowledge")
      .setPayload(RuleCollisionAcknowledgmentRequest)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
